import { format, getYear } from "date-fns";

/**
 * Geometry + scale helpers for the vitals trend charts.
 *
 * Kept free of React/recharts so the axis rules (slot count, y-domain,
 * date-label placement) stay unit-testable — see vitalsChart.spec.ts.
 */

/** Breathing room between a plotted point and the plot edge, on every side. */
export const CHART_INSET = 24;
/** A slot narrower than this is unreadable — slot count shrinks instead. */
export const SLOT_MIN_WIDTH = 60;
export const MIN_SLOTS = 5;
export const MAX_SLOTS = 10;
/** Must mirror <YAxis width={...} /> in the chart panels. */
export const Y_AXIS_WIDTH = 32;
/** Right-hand margin — keeps the reference-band labels off the card edge. */
export const CHART_RIGHT_MARGIN = 8;
export const CHART_HEIGHT = 220;
/** Height reserved below the plot for the date labels. */
export const CHART_BOTTOM_MARGIN = 24;

/** BP axis contract: default 40–160 mmHg, gridline every 40. */
export const BP_AXIS = {
  step: 40,
  minUpper: 160,
  maxLower: 40,
} as const;

/** Widest y-axis the tick generator will emit before it gives up on `step`. */
const MAX_TICKS = 24;

export interface YAxisScale {
  domain: [number, number];
  ticks: number[];
}

export interface YAxisOptions {
  /** Gridline interval. Derived from the data when omitted. */
  step?: number;
  /** Domain upper bound is never below this. */
  minUpper?: number;
  /** Domain lower bound is never above this. */
  maxLower?: number;
}

/** Trims binary-float noise introduced by fractional steps (e.g. 0.1 + 0.2). */
function roundTo(value: number, precision = 6): number {
  return Number(value.toFixed(precision));
}

/** Rounds a rough interval up to the nearest 1/2/5 × 10ⁿ so ticks read cleanly. */
function niceStep(extent: number): number {
  if (!Number.isFinite(extent) || extent <= 0) return 1;
  const rough = extent / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  const multiplier =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return roundTo(multiplier * magnitude);
}

/**
 * Builds a y-scale that always keeps the data strictly inside the plot — the
 * domain snaps outward to the next `step` boundary, so no point can ever land
 * on the top or bottom edge of the chart.
 */
export function computeYAxis(
  values: Array<number | undefined | null>,
  options: YAxisOptions = {}
): YAxisScale {
  const { minUpper, maxLower } = options;
  const finite = values.filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v)
  );

  if (finite.length === 0) {
    const lower = maxLower ?? 0;
    const upper = minUpper ?? lower + (options.step ?? 1) * 4;
    return {
      domain: [lower, upper],
      ticks: buildTicks(lower, upper, options.step),
    };
  }

  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const step = options.step ?? niceStep(max - min || Math.abs(max) || 1);

  // "Strictly above / strictly below" — a value sitting exactly on a boundary
  // pushes the domain out by a further step so it never touches the edge.
  let upper = roundTo((Math.floor(max / step) + 1) * step);
  let lower = roundTo((Math.ceil(min / step) - 1) * step);
  if (minUpper != null) upper = Math.max(upper, minUpper);
  if (maxLower != null) lower = Math.min(lower, maxLower);

  return { domain: [lower, upper], ticks: buildTicks(lower, upper, step) };
}

function buildTicks(lower: number, upper: number, step?: number): number[] {
  if (!step || step <= 0) return [lower, upper];
  const count = Math.round((upper - lower) / step);
  if (count < 1 || count > MAX_TICKS) return [lower, upper];
  return Array.from({ length: count + 1 }, (_, i) => roundTo(lower + i * step));
}

/** Plot area available to the series, once the y-axis and margin are taken out. */
export function computePlotWidth(viewportWidth: number): number {
  return Math.max(0, viewportWidth - Y_AXIS_WIDTH - CHART_RIGHT_MARGIN);
}

/** How many readings fit across the plot at a comfortable density. */
export function computeSlotCount(plotWidth: number): number {
  const raw = Math.floor((plotWidth || 0) / SLOT_MIN_WIDTH);
  if (!Number.isFinite(raw)) return MIN_SLOTS;
  return Math.min(MAX_SLOTS, Math.max(MIN_SLOTS, raw));
}

/**
 * Distance between adjacent readings. Points are inset by CHART_INSET on both
 * sides, so `slotCount` readings span the plot exactly once the insets are
 * removed — the same pitch is then reused when the chart overflows and scrolls.
 */
export function computeSlotPitch(plotWidth: number, slotCount: number): number {
  const usable = Math.max(0, plotWidth - CHART_INSET * 2);
  return usable / Math.max(1, slotCount - 1);
}

/**
 * SVG width for the chart: exactly the viewport while the readings fit, and
 * one pitch per extra reading beyond that (which is what makes it scroll).
 */
export function computeChartWidth(
  viewportWidth: number,
  pointCount: number,
  slotCount: number
): number {
  if (pointCount <= slotCount) return viewportWidth;
  const pitch = computeSlotPitch(computePlotWidth(viewportWidth), slotCount);
  return (
    Y_AXIS_WIDTH +
    CHART_RIGHT_MARGIN +
    CHART_INSET * 2 +
    (pointCount - 1) * pitch
  );
}

/**
 * Right-aligns the readings by prepending empty slots. Below the slot count the
 * chart leaves the unused space on the left rather than stretching to fill it.
 */
export function padSlots<T>(
  points: T[],
  slotCount: number,
  makePad: (index: number) => T
): T[] {
  const missing = slotCount - points.length;
  if (missing <= 0) return points;
  return [...Array.from({ length: missing }, (_, i) => makePad(i)), ...points];
}

/** Formats an axis date, adding a "'25" suffix only outside the current year. */
export function formatAxisDate(date: Date, now: Date = new Date()): string {
  return getYear(date) === getYear(now)
    ? format(date, "d MMM")
    : format(date, "d MMM ''yy");
}

/**
 * One label per distinct date, sat under the leftmost reading of that date.
 * `times` is slot-ordered (oldest first) with `null` for padding slots.
 */
export function buildDateAxisLabels(
  times: Array<number | null | undefined>,
  now: Date = new Date()
): Array<string | null> {
  const seen = new Set<string>();
  return times.map((time) => {
    if (time == null) return null;
    const date = new Date(time);
    const key = format(date, "yyyy-MM-dd");
    if (seen.has(key)) return null;
    seen.add(key);
    return formatAxisDate(date, now);
  });
}
